import { c as _c } from "react/compiler-runtime";
import { Input } from "uiShadcn/input";
import { useFormMode } from "forms/contexts/useFormModeContext";
import useFile from "forms/fields/File/useFile";
import FormItem from "forms/fields/FormItem";
import { Loader } from "lucide-react";
import { forwardRef } from "react";
import { useIntl } from "react-intl";
import { FormModeValues } from "types/formMode";
import UploadProgressBar, { type UploadProgressUiState } from "ui/UploadProgressBar/UploadProgressBar";
interface FileSizeHintProps {
  maxSizeInMB: number;
  maxSizeInMBForMp4?: number;
}
function FileSizeHint(t0) {
  const $ = _c(9);
  const {
    maxSizeInMB,
    maxSizeInMBForMp4
  } = t0;
  const {
    formatMessage
  } = useIntl();
  let t1;
  if ($[0] !== formatMessage || $[1] !== maxSizeInMB) {
    t1 = formatMessage({
      id: "fileSize"
    }, {
      size: maxSizeInMB
    });
    $[0] = formatMessage;
    $[1] = maxSizeInMB;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== formatMessage || $[4] !== maxSizeInMBForMp4) {
    t2 = maxSizeInMBForMp4 ? ` ${formatMessage({
      id: "fileSizeMp4"
    }, {
      size: maxSizeInMBForMp4
    })}` : "";
    $[3] = formatMessage;
    $[4] = maxSizeInMBForMp4;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <span className="inline-block pt-n2 font-bold">{t1}{t2}</span>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function FileUploadStatus(t0) {
  const $ = _c(13);
  const {
    isUploading,
    uploadProgress
  } = t0;
  const {
    formatMessage
  } = useIntl();
  if (uploadProgress != null) {
    let t1;
    if ($[0] !== formatMessage) {
      t1 = formatMessage({
        id: "fileUpload"
      });
      $[0] = formatMessage;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== formatMessage) {
      t2 = formatMessage({
        id: "fileUploadProcessing"
      });
      $[2] = formatMessage;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1 || $[5] !== t2 || $[6] !== uploadProgress) {
      t3 = <UploadProgressBar progress={uploadProgress} label={t1} processingText={t2} className="pt-n2" />;
      $[4] = t1;
      $[5] = t2;
      $[6] = uploadProgress;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  if (isUploading) {
    let t1;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader size={16} className="animate-spin" aria-hidden={true} />;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== formatMessage) {
      t2 = formatMessage({
        id: "fileUpload"
      });
      $[9] = formatMessage;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    let t3;
    if ($[11] !== t2) {
      t3 = <output className="flex items-center gap-2 pt-n2 text-font-2 text-xs" aria-live="polite">{t1}<span>{t2}</span></output>;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  return null;
}
interface FileBodyProps {
  name: string;
  onChange: (file: {
    id: string;
  } | null) => void;
  isRequired: boolean;
  validators?: Record<string, unknown>[];
  disabled?: boolean;
}
const FileBody = forwardRef<HTMLInputElement, FileBodyProps>((t0, ref) => {
  const $ = _c(27);
  const {
    name,
    validators,
    onChange: t1,
    isRequired,
    disabled
  } = t0;
  const onChange = t1 === undefined ? _temp : t1;
  const {
    formatMessage
  } = useIntl();
  let t2;
  if ($[0] !== onChange || $[1] !== validators) {
    t2 = {
      onChange,
      validators
    };
    $[0] = onChange;
    $[1] = validators;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    allowedExtensions,
    maxSizeInMB,
    maxSizeInMBForMp4,
    onChangeHandler,
    isUploading,
    uploadProgress
  } = useFile(t2);
  const formMode = useFormMode();
  if (!name) {
    return null;
  }
  let t3;
  if ($[3] !== allowedExtensions || $[4] !== formatMessage) {
    t3 = formatMessage({
      id: "fileFormats"
    }, {
      formats: allowedExtensions
    });
    $[3] = allowedExtensions;
    $[4] = formatMessage;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = `${t3}`;
  let t5;
  if ($[6] !== maxSizeInMB || $[7] !== maxSizeInMBForMp4) {
    t5 = <FileSizeHint maxSizeInMB={maxSizeInMB} maxSizeInMBForMp4={maxSizeInMBForMp4} />;
    $[6] = maxSizeInMB;
    $[7] = maxSizeInMBForMp4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isUploading || $[10] !== uploadProgress) {
    t6 = <FileUploadStatus isUploading={isUploading} uploadProgress={uploadProgress} />;
    $[9] = isUploading;
    $[10] = uploadProgress;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `File-${name}`;
  let t8;
  if ($[12] !== onChangeHandler) {
    t8 = e => onChangeHandler(e.target.files?.[0] ?? null);
    $[12] = onChangeHandler;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = formMode === FormModeValues.Preview || disabled || isUploading;
  let t10;
  if ($[14] !== allowedExtensions || $[15] !== isRequired || $[16] !== ref || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <Input ref={ref} dataTestId={t7} type="file" accept={allowedExtensions} onChange={t8} disabled={t9} required={isRequired} />;
    $[14] = allowedExtensions;
    $[15] = isRequired;
    $[16] = ref;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isRequired || $[22] !== t10 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t11 = <FormItem label={t4} required={isRequired} extraContent={t5} labelClassName="w-full max-w-full flex-wrap items-baseline" belowControl={t6}>{t10}</FormItem>;
    $[21] = isRequired;
    $[22] = t10;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
});
export default FileBody;
function _temp() {}